import React from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { cptServiceLabel, renderClientName, renderDateRange, SaveParams } from 'utils';
import {
    DateTypeSelect,
    DateTypeSelector,
    DownloadCsv,
    FilterPopover,
    SelectAutocompletePagination,
    SelectedFilters,
} from 'components';
import { invoiceTabEnums } from './constants';

/**
 * Filter controls and active-filter chips for the Invoices tabs.
 *
 * Two exports, because they occupy two different slots: `InvoiceFilters` is the filter-icon button
 * in the header row, `InvoiceSelectedFilters` is the chip bar above whichever tab's table is
 * showing. They live in one file so the set of state keys — which one writes and the other charts
 * and clears — cannot drift.
 *
 * Only the panel filters are in the popover. Service Code keeps its column-header dropdown on the
 * Invoiceable table, the same split the schedule uses; the chip bar unifies the two sets.
 */

/** Panel-only keys, and their display-name companions. What the popover's "Reset all" clears. */
const POPOVER_KEYS = ['client', 'clientFull', 'clientFullName', 'from', 'to', 'invoiceDate'];

/** Tabs that carry an invoice (rather than the not-yet-invoiced appointments), so have an Invoice Date. */
const isInvoicedTab = (tabType) =>
    tabType === invoiceTabEnums.OPEN || tabType === invoiceTabEnums.COMPLETED || tabType === invoiceTabEnums.PENDING;

export const InvoiceFilters = ({}) => {
    const navigate = useNavigate();
    const location = useLocation();
    const pushInfo = location?.state;
    const checkInvoiceAble = isInvoicedTab(pushInfo?.tabType);

    const activeFiltersCount =
        (pushInfo?.client ? 1 : 0) +
        (pushInfo?.from || pushInfo?.to ? 1 : 0) +
        (checkInvoiceAble && pushInfo?.invoiceDate ? 1 : 0);

    const handleChange = (e) => {
        const info = {
            ...pushInfo,
        };
        info.skip = 0;
        info.limit = 50;
        info.page = 1;
        if (e.target.value === 'All' || !e.target.value) {
            delete info[e.target.name];
            // Drop the display-name companion with its id, or the chip keeps labelling a filter
            // that is gone.
            if (e.target.name === 'client') {
                delete info.clientFull;
                delete info.clientFullName;
            }
        } else {
            info[e.target.name] = e.target.value;
            if (e.target.name === 'client') {
                info['clientFull'] = e.target?.full;
            }
        }
        SaveParams(navigate, { ...info });
    };

    const handleResetAll = (handleClose) => {
        const params = { ...pushInfo, skip: 0, limit: 50, page: 1 };
        POPOVER_KEYS.forEach((k) => delete params[k]);
        SaveParams(navigate, params);
        handleClose?.();
    };

    return (
        <div className="table-invoice-filter-section">
            <div className="billing-invoices-filter-box" style={{ alignItems: 'flex-end' }}>
                <FilterPopover activeFiltersCount={activeFiltersCount} onResetAll={handleResetAll}>
                    <SelectAutocompletePagination
                        outLabel={'Client'}
                        name={'client'}
                        handleSelect={handleChange}
                        placeholder={pushInfo?.clientFullName ? pushInfo?.clientFullName : 'All'}
                        defaultValue={pushInfo?.client}
                        renderValue={(i) => renderClientName(i)}
                        type={'client'}
                        uiType={'tableFilter'}
                        noError={true}
                        currentSelected={pushInfo?.clientFull && pushInfo?.clientFull}
                    />
                    <DateTypeSelector startName={'from'} endName={'to'} outLabel={'DOS'} />
                    {checkInvoiceAble && <DateTypeSelect name={'invoiceDate'} noError={true} outLabel={'Invoice Date'} />}
                </FilterPopover>

                {checkInvoiceAble && <DownloadCsv type={'billingInvoice'} fromModal={true} />}
            </div>
        </div>
    );
};

export const InvoiceSelectedFilters = () => {
    const location = useLocation();
    const info = location?.state;
    const tagList = useSelector((state) => state.tags.tagList);
    const globalServices = useSelector((state) => state.fundingSource.fundingUniqueServices);

    const filtersList = [
        {
            name: 'Client',
            key: 'client',
            companions: ['clientFull', 'clientFullName'],
            render: (v, state) => renderClientName(state?.clientFull),
        },
        { name: 'DOS', keys: ['from', 'to'], render: ([from, to]) => renderDateRange(from, to) },
        {
            name: 'Invoice Date',
            key: 'invoiceDate',
            show: isInvoicedTab(info?.tabType),
            render: (v) => renderDateRange(v, null),
        },
        { name: 'Service', key: 'cptCode', render: (id) => cptServiceLabel(globalServices, id?.id ?? id) },
        { name: 'Tag', key: 'tags', options: tagList, loading: !tagList?.length },
        { name: 'Search', key: 'searchValue', companions: ['searchKey'] },
    ];

    return <SelectedFilters filtersList={filtersList} resetParams={{ skip: 0, limit: 50, page: 1 }} doNotCrop />;
};
